import utils from '@/helpers/utils/index.js'
import { FORM_CONSTANTS } from '@/components/DataGrid/FormBuilder/config/constants'
import storage from '@/services/storage'
import store from '@/store'
import { sortBy, cloneDeep, each, pick, find } from 'lodash'
import { v1 as uuidv1 } from 'uuid'
import eventBus from '@/helpers/event-bus'

export default function (_this) {
  return {
    /**
     * Get model to save values
     * @param {String} st - storage type
     * @returns {function} set model function
     */
    getWriteModel(st) {
      let model

      switch (st) {
        case 'user':
          model = window.$setFBUserModel
          break
        case 'dashboard':
          model = window.$setDashboardModel
          break
        case 'workspace':
          model = window.$setFBWorkspaceData
          break
      }

      return model
    },
    /**
     * Get function that will read data from appropriate storage
     * @param {String} st - storage type
     * @returns {function} model function to read data
     */
    getReadModel(st) {
      let model

      switch (st) {
        case 'user':
          model = window.$FBUserModel
          break
        case 'dashboard':
          model = window.$getDashboardModel
          break
        case 'workspace':
          model = window.$getWorkspaceData
          break
      }

      return model
    },
    // document it later when have time
    updateGrid(gridId, gridRows) {
      if (typeof gridRows !== 'object')
        throw new Error(`[DataGrid update] - "gridRows" must be object of rows`)
      if (Array.isArray(gridRows))
        throw new Error(`[DataGrid update] - "gridRows" can't be array`)

      const sanitizedGrid = {}
      const meta = store.state.dataGrid_options[gridId].meta

      for (let rowKey in gridRows) {
        if (!gridRows[rowKey]) {
          sanitizedGrid[rowKey] = null
        } else {
          sanitizedGrid[rowKey] = {}

          for (let cellKey in meta) {
            if (typeof gridRows[rowKey][cellKey] === 'undefined') continue

            sanitizedGrid[rowKey][cellKey] = gridRows[rowKey][cellKey]
          }

          sanitizedGrid[rowKey].rowKey = rowKey
          sanitizedGrid[rowKey]._id = gridRows[rowKey]._id || null
        }
      }

      return new Promise((resolve) => {
        _this.dashboardModels
          .updateDashboardModel(`${gridId}`, sanitizedGrid)
          .then(() => {
            store.dispatch('dataGrid_logGridChange', {
              gridId,
              value: sanitizedGrid,
            })

            resolve()
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
    /**
     * Get last id of DataGrid rows
     * @param {String} gridId - DataGrid id
     */
    getLastId(gridId) {
      return new Promise((resolve) => {
        _this
          .getRef(`models/${gridId}`)
          .orderByChild('_id')
          .limitToLast(1)
          .once('value', (snapshot) => {
            const lastRow = snapshot.val()

            let id = lastRow ? lastRow[Object.keys(lastRow)[0]]['_id'] || 0 : 0
            resolve(++id)
          })
      })
    },
    /**
     * Create new row in DataGrid model
     * @param {String} gridId - DataGrid id
     * @return {Promise}
     */
    async addRow(gridId, rowVals, sync) {
      return new Promise(async (resolve, reject) => {
        const id = await _this.dataGrid.data.getLastId(gridId)

        const rowKey = uuidv1(Date.now())
        let newRow = Object.assign(
          typeof rowVals === 'object' ? rowVals : {},
          { rowKey, _id: id }
        )
        const ref = _this.getRef(`models/${gridId}/${rowKey}`)
        const dgOptions = store.state.dataGrid_options[gridId]

        if (gridId === 'pages' && !sync) newRow = await utils.corePagesGridSync.rowAdded(newRow)

        if (dgOptions.meta && dgOptions.meta.group) {
          newRow.group = getUserGroup(store.state)
        } else {
          newRow.group = false
        }
        ref.transaction(
          () => {
            return newRow
          },
          (err, committed, snapshot) => {
            if (err) {
              console.error(`[DataGrid addRow] - couldn't create row`, err)
              reject()
            } else if (!committed) {
              console.error(`[DataGrid addRow] - commit failed`)
              reject()
            } else {
              store.dispatch('dataGrid_logRowChange', {
                gridId,
                rowKey,
                id,
                value: newRow,
              })

              resolve(rowKey)
              // emit simple-workflow event
              eventBus.$emit(`simpleWorkflow-rowAdded-${gridId}`)
            }
          }
        )
      })
    },
    /**
     * Delete row from DataGrid
     * @param {String} gridId - DataGrid id
     * @param {String} rowKey - key of row to delete
     * @return {Promise}
     */
    removeRow(gridId, rowKey, sync) {
      return new Promise((resolve) => {
        if (gridId === 'pages' && !sync) utils.corePagesGridSync.removeRow(rowKey)

        _this.dashboardModels
          .setDashboardModel(`${gridId}/${rowKey}`, null)
          .then(() => {
            store.dispatch('dataGrid_logRowChange', {
              gridId,
              rowKey,
              value: null,
            })

            resolve()
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
    /**
     * Get options of appropriate data grid
     * @param {String} gridId - grid id
     * @return {Promise}
     */
    getOptions(gridId) {
      return new Promise((resolve, reject) => {
        _this
          .getRef(`widgetsData/dataGrid/${gridId}`)
          .once('value', (snapshot, err) => {
            if (err) reject(err)
            else resolve(snapshot.val())
          })
      })
    },
    /**
     * Rename column display name
     * @param {String} gridId - grid key
     * @param {String} cellKey - column name key
     * @param {String} newVal - column name value
     * @param {Any} template - current template key or null
     */
    async saveColumnNameVal(gridId, cellKey, newVal, template) {
      let rowEditorFieldsForm = (
        await _this
          .getRef('widgetsData/dataGrid')
          .child(gridId)
          .child('rowEditorFieldsForm')
          .once('value')
      ).val()

      if (!rowEditorFieldsForm && template) {
        rowEditorFieldsForm = (
          await _this.root
            .ref('dashboards')
            .child(template)
            .child('widgetsData/dataGrid')
            .child(gridId)
            .child('rowEditorFieldsForm')
            .once('value')
        ).val()
      }

      if (!rowEditorFieldsForm) {
        console.error(
          `[rowEditorFieldsForm] - could't find form in grid "${gridId}"`
        )
        return
      }

      const formData = JSON.parse(rowEditorFieldsForm)
      let control

      for (let section of formData.sections) {
        for (let row of section.rows) {
          control = find(row.controls, { propName: cellKey })

          if (control) break
        }
        if (control) break
      }

      if (!control) {
        console.error(`[control] - could't find control in grid "${cellKey}"`)
        return
      }

      control.label = newVal

      _this
        .getRef('widgetsData/dataGrid')
        .child(gridId)
        .child('rowEditorFieldsForm')
        .set(JSON.stringify(formData))

      _this
        .getRef(`widgetsData/dataGrid/${gridId}/meta/${cellKey}/displayName`)
        .set(newVal)
    },
    /**
     * Remove column
     * @param {Object} options - column options collection
     * @param {String} gridId - grid id
     * @param {String} cellKey - column name key
     */
    async removeColumn(gridId, cellKeyToDelete) {
      const options = await new Promise((resolve) => {
        _this.dataGrid.options.read(gridId, (o) => {
          resolve(o)
        })
      })

      function removeColumnFromMeta(meta) {
        let curMeta = cloneDeep(meta)
        const newMeta = {}

        for (let cellKey in curMeta) {
          curMeta[cellKey].key = cellKey
        }

        delete curMeta[cellKeyToDelete]

        curMeta = sortBy(curMeta, ['order'])

        for (let idx in curMeta) {
          if (idx === 'rmIndex') continue

          curMeta[idx].order = idx
          newMeta[curMeta[idx].key] = curMeta[idx]
          delete newMeta[curMeta[idx].key].key
        }

        return newMeta
      }

      options.meta = removeColumnFromMeta(options.meta)

      if (options.views) {
        for (let viewKey in options.views) {
          const view = options.views[viewKey]

          view.meta = removeColumnFromMeta(view.meta)
        }
      }

      await _this.dataGrid.options.setOptions(gridId, options)

      this.removeControl(options, gridId, cellKeyToDelete)
    },
    removeControl(opts, gridId, propName) {
      let sectionIdx
      let rowIdx
      let controlIdx
      const formData = JSON.parse(opts.rowEditorFieldsForm)

      for (let sIdx in formData.sections) {
        let section = formData.sections[sIdx]
        for (let rIdx in section.rows) {
          let row = section.rows[rIdx]
          for (let cIdx in row.controls) {
            if (row.controls[cIdx].propName === propName) {
              sectionIdx = sIdx
              rowIdx = rIdx
              controlIdx = cIdx
              break
            }
          }
        }
      }

      formData.sections[sectionIdx].rows[rowIdx].controls.splice(controlIdx, 1)

      _this.dataGrid.options.saveRowEditorForm(gridId, formData, () => {
        eventBus.$emit(`dgForm-${gridId}-updated`)
      })
    },
    /**
     * Set display column for grid
     * @param {String} gridId - grid id
     * @param {String} cellKey - column key
     */
    setAsDisplayColumn(gridId, cellKey) {
      _this
        .getRef(`widgetsData/dataGrid/${gridId}/displayColumn`)
        .set(cellKey, (err) => {
          if (err) console.error(err)
        })
    },
    /**
     * Save new cell value
     * @param {Object} data - new data collection
     */
    saveCellVal(data, sync) {
      const { options, rowKey, cellKey, value } = data
      const gridId = options.source

      let objToSend = {
        gridId,
        rowKey,
        cellKey,
        value,
      }

      if(data.isLookup){
        objToSend.isLookup = data.isLookup
      }

      return new Promise((resolve) => {
        _this.dashboardModels
          .setDashboardModel(`${gridId}/${rowKey}/${cellKey}`, value)
          .then(() => {
            store.dispatch('dataGrid_logCellChange', objToSend)

            if (gridId === 'pages' && !sync) utils.corePagesGridSync.cellChanged(rowKey, cellKey)

            resolve()

            eventBus.$emit(`simpleWorkflow-fieldUpdated-${gridId}-${cellKey}`, $dataGrid(gridId)[rowKey])
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
    /**
     * Remove meta data
     * @param {String} gridId - grid id
     */
    removeMeta(gridId) {
      _this.getRef(`widgetsData/dataGrid/${gridId}/meta`).remove()
    },
    /**
     * Set new meta data for grid
     * @param {String} gridId - grid id
     * @param {Object} metaData - metaData
     */
    setNewMeta(gridId, metaData) {
      _this.getRef(`widgetsData/dataGrid/${gridId}/meta`).set(metaData)
    },
    /**
     * Remove display column
     * @param {String} gridId - grid id
     */
    removeDisplayColumn(gridId) {
      _this.getRef(`widgetsData/dataGrid/${gridId}/displayColumn`).remove()
    },
    /**
     * Add new column
     * @param {Object} data - column data
     */
    async addColumn(data, rowIndex, sectionIndex) {
      const { type, gridId, displayName, propertyName } = data
      const options = await new Promise((resolve) => {
        _this.dataGrid.options.read(gridId, (o) => {
          resolve(o || {})
        })
      })
      const template = store.state.currentTemplate

      if (!options.rowEditorFieldsForm && template) {
        const templateForm = await new Promise((resolve) => {
          _this.root
            .ref('dashboards')
            .child(template)
            .child('widgetsData/dataGrid')
            .child(gridId)
            .child('rowEditorFieldsForm')
            .once('value', (snapshot) => {
              resolve(snapshot.val())
            })
        })

        options.rowEditorFieldsForm = templateForm
      }

      if (!options.meta) {
        options.meta = {}

        if (template) {
          const templateMeta = await new Promise((resolve) => {
            _this.root
              .ref('dashboards')
              .child(template)
              .child('widgetsData/dataGrid')
              .child(gridId)
              .child('meta')
              .once('value', (snapshot) => {
                resolve(snapshot.val())
              })
          })

          if (templateMeta) options.meta = { ...templateMeta }
        }
      }

      const order = utils.count(options.meta)
      const newColumnInMeta = {
        displayName,
        type,
        order,
      }

      if (data.lookUpTable) newColumnInMeta.lookUpTable = data.lookUpTable
      if (data.workflow){
        newColumnInMeta.workflow = data.workflow
        newColumnInMeta.startTitle = data.startTitle
      }
      if (type === 'datagrid') {
        newColumnInMeta.dataGrid = data.dataGrid
        newColumnInMeta.filterColumn = data.filterColumn
      }

      options.meta[propertyName] = newColumnInMeta

      if (options.views) {
        for (let viewKey in options.views) {
          options.views[viewKey].meta[propertyName] = pick(newColumnInMeta, [
            'order',
          ])
        }
      }

      _this.dataGrid.options.setOptions(gridId, options)

      if (data.pasteInNewRow) {
        this.addFormBuilderControlInNewRow(data, options)
      } else {
        this.addFormBuilderControl(data, options, rowIndex, sectionIndex)
      }
    },
    async addFormBuilderControl(data, options, rowIndex, sectionIndex) {
      if (!options) await store.state.dataGrid_optons[data.gridId]
      if (!rowIndex || !sectionIndex) {
        sectionIndex = 0
        rowIndex = 0
      }

      try {
        const controlInfo = data.type === 'datagrid'
          ? this.prepareFormBuilderControl(data, 12)
          : this.prepareFormBuilderControl(data, 6)
        const form = JSON.parse(options.rowEditorFieldsForm)

        // add to row
        form.sections[sectionIndex].rows[rowIndex].controls.push(controlInfo)

        _this.dataGrid.options.saveRowEditorForm(data.gridId, form, () => {
          eventBus.$emit(`dgForm-${data.gridId}-updated`)
        })
      } catch (err) {
        console.error(err)
      }
    },
    addFormBuilderControlInNewRow(data, options) {
      function addRow(form) {
        var rowInfo = _.cloneDeep(FORM_CONSTANTS.Row);

        // general row_name (id)
        rowInfo.name = _.domUniqueID(form.sections[0].name + '_row_');

        form.sections[0].rows.push(rowInfo);

        const rowIdx = form.sections[0].rows.length - 1

        form.sections[0].rows[rowIdx].controls = []

        return rowIdx
      }

      try {
        const controlInfo = this.prepareFormBuilderControl(data, 12)
        const form = JSON.parse(options.rowEditorFieldsForm)
        const rowIdx = addRow(form)

        // add to row
        form.sections[0].rows[rowIdx].controls.push(controlInfo)

        _this.dataGrid.options.saveRowEditorForm(data.gridId, form, () => {
          eventBus.$emit(`dgForm-${data.gridId}-updated`)
        })
      } catch (err) {
        console.error(err)
      }
    },

    // helper functions

    prepareFormBuilderControl(data, width) {
      function getControlType(type) {
        switch (type) {
          case 'string':
            return 'text'
          case 'date':
            return 'datepicker'
        }
        return type
      }

      const { type, displayName, propertyName } = data

      const controlType = getControlType(type)
      let controlInfo = cloneDeep(FORM_CONSTANTS.Control)
      let controlTypes = cloneDeep(FORM_CONSTANTS.Type)
      controlInfo.type = getControlType(type)

      // extending
      controlTypes.lookup = { label: 'Look Up' }
      controlTypes.multi_lookup = { label: 'Multi Look Up' }
      controlTypes.files = { label: 'Files' }
      controlTypes.custom = { label: 'Custom code' }
      controlTypes.user = { label: 'Users' }
      controlTypes.multiline_text = { label: 'Multiline Text' }
      controlTypes.profile_image = { label: 'Profile Image' }
      controlTypes.datagrid = { label: 'DataGrid' }
      controlTypes.workflow = { label: 'Workflow' }
      controlTypes.text_editor = { label: 'Text Editor' }

      // generate id
      controlInfo.name = _.domUniqueID(`control_${controlType}_`)
      controlInfo.label = controlTypes[controlType].label
      controlInfo.fieldName = controlInfo.name // same for both

      // check if there's any more special fields?
      if (controlTypes[controlType].other_properties) {
        each(controlTypes[controlType].other_properties, (value, key) => {
          controlInfo[key] = value
        })
      }

      controlInfo = Object.assign(controlInfo, {
        className: `col-md-${width}`,
        propName: propertyName,
        label: displayName,
      })

      if (type === 'lookup' || type === 'multi_lookup')
        controlInfo.ableToAddLookUpVal = true

      return controlInfo
    },
    setDataGridVal() {
      /**
       *
       * @param {String} gridId - grid id
       * @param {String} rowAndCell - concatination of row and cell to find value address
       * @param {Object} value - value to save
       */
      return (gridId, rowAndCell, value) => {
        return new Promise(async (resolve) => {
          const options = store.state.dataGrid_options[gridId]

          if (!options) {
            console.error(
              `[DataGrid Error]: grid id "${gridId}" not found in database`
            )
            return
          }

          const rowAndCellSplited = rowAndCell.split('.')

          if (!rowAndCellSplited[0] || !rowAndCellSplited[1]) {
            console.error(
              `[DataGrid Error]: row and cell address "${rowAndCell}" is invalid`
            )
            return
          }

          const getModel = this.getReadModel(options.storageType)
          const tData = getModel(options.source)
          const rowKey = rowAndCellSplited[0]
          const cellKey = rowAndCellSplited[1]

          if (!tData[rowKey]) {
            console.error(
              `[DataGrid Error]: row and cell address "${rowAndCell}" not found`
            )
            return
          }

          const data = {
            options,
            rowKey,
            cellKey,
            value,
          }

          this.saveCellVal(data).then(resolve)
        })
      }
    },
    /**
     * Upload files to storage and insert appropriate names into a cell value
     * @param {Object} data - required data
     * @param {function} cb - callback function
     */
    async uploadFiles(data, cb) {
      const { dashId, files, curFiles } = data

      const uploadFiles = await new Promise((resolve) => {
        storage.upload(dashId, files, (f) => {
          resolve(f)
        })
      })

      await _this.files.add(uploadFiles)

      const value = (curFiles ? curFiles.split(',') : []).concat(
        Object.keys(uploadFiles)
      )

      this.saveCellVal({
        ...data,
        value: value.join(','),
      })

      cb()
    },
    getFileList(data) {
      const { rowKey, cellKey, gridId } = data

      return new Promise(async (resolve) => {
        const options = await new Promise((resolve) => {
          this.getOptions(gridId, (err, gridOptions) => {
            resolve(gridOptions)
          })
        })

        const getModel = this.getReadModel(options.storageType)
        const tData = getModel(options.source)

        const cell = tData[rowKey][cellKey]

        if (cell) resolve(cell.value)
        else resolve(null)
      })
    },
    /**
     * Delete appropriate file
     * @param {Object} data - required data
     * @param {function} cb - callback function
     */
    async deleteFile(data, cb) {
      const { rowKey, cellKey, options, fileName, fileKey, dashId } = data

      const setModel = this.getWriteModel(options.storageType)
      const getModel = this.getReadModel(options.storageType)

      const tData = getModel(options.source)

      storage.delete(dashId, fileName, async (err) => {
        if (err) {
          console.error(err)
          cb()
          return
        }

        delete tData[rowKey][cellKey].value[fileKey]

        await setModel(options.source, tData)

        cb()
      })
    },
    /**
     * Save new row data to database
     * @param {String} gridId - DataGrid id
     * @param {String} rowKey - row key
     * @param {Object} newRowData - new row data
     */
    saveRowData(gridId, rowKey, newRowData) {
      return new Promise(resolve => {
        const meta = store.state.dataGrid_options[gridId].meta
        const rowDataToSave = {}

        for (let cellKey in newRowData) {
          if (cellKey in meta || cellKey === '_created')
            rowDataToSave[cellKey] = newRowData[cellKey]
        }

        _this.getRef('models')
          .child(gridId)
          .child(rowKey)
          .update(rowDataToSave, () => {
            store.dispatch('dataGrid_logRowChange', {
              gridId,
              rowKey,
              id: newRowData._id,
              value: rowDataToSave,
            })

            resolve()
          })
      })
    },
  }
}

function getUserGroup(state) {
  const users = state.models_dashboardModel.users

  if (!users) return null

  const userGroup = find(users, { user: state.userId })

  if (!userGroup) return null

  return userGroup.group ? userGroup.group : null
}
